import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import { useModalManager } from '../../../../hooks/ModalManager.hook';
import { useAppDispatch } from '../../../../hooks/useAppDispatcher.hook';

import {
  createTagControlThunk,
  deleteTagControlThunk,
  getTagControlListThunk,
  updateTagControlThunk,
} from '../../../../redux/ControlPanel/TagControl/TagControl.thunk';

import CardComponent from '../../../../components/Card.component';
import ButtonComponent from '../../../../components/Button.component';
import InputComponent from '../../../../components/Input/Input.component';
import SpinnerComponent from '../../../../components/Spinner.component';
import ControlCreateEditModal, {
  ControlPanelItemFormValues,
  ControlPanelItemFormValuesWithId,
} from '../../../../components/Modal/Control/ControlCreateEditModal';
import ConfirmDuplicateModal from '../../../../components/Modal/Control/ConfirmDuplicateModal';
import ControlSectionContent from './ControlSectionContent.component';
import { SortTypes } from '../../../../types/sort.types';

import SearchIcon from '../../../../components/Icons/Search.icon';
import SortArrowsIcon from '../../../../components/Icons/SortArrows.icon';

import { ControlSectionTypes } from '../../../../redux/ControlPanel/ControlPanelList/ControlPanelList.type';

import {
  StyledControlListItem,
  StyledControlSectionContainer,
  StyledControlSectionHeader,
  StyledHeaderButtonsWrapper,
  StyledHeaderLabel,
  StyledHeaderTitle,
  StyledSearchIcon,
  StyledSearchWrapper,
  StyledSortContainer,
} from './sharedStyledComponents';
import { checkUserRequiredPermissions } from '../../../../utils/getUserRolesByToken';
import { UserPermissions } from '../../../../types/userPermissions.types';
import useAuthHook from '../../../../hooks/useAuth.hook';
import { useSelector } from 'react-redux';
import {
  getTagControlList,
  getTagControlListPagination,
  setTagControlList,
} from '../../../../redux/ControlPanel/TagControl/TagControl.slice';
import { Tag } from '../../../../redux/ControlPanel/TagControl/TagControl.type';

const sectionType = ControlSectionTypes.TAGS;

export const TagsSectionComponent: FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuthHook();
  const { openModal } = useModalManager();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [orderSort, setOrderSort] = useState(SortTypes.ASC);

  const tags = useSelector(getTagControlList);
  const pagination = useSelector(getTagControlListPagination);

  const editable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_EDIT_TAG,
  ]);

  const creatable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_CREATE_TAG,
  ]);

  const deletable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_DELETE_TAG,
  ]);

  const viewable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_VIEW_TAG,
  ]);

  const fetchParams = useMemo(() => {
    return {
      q: searchQuery,
      sort: `${orderSort === SortTypes.DESC ? '-' : ''}name`,
    };
  }, [orderSort, searchQuery]);

  const createItemWithModalWrapper = async (
    fetchFunc: Function,
    updateFunc: Function,
    payload: any,
    updateFetchParams?: any,
    message?: string,
  ) => {
    dispatch(fetchFunc(payload)).then((res: any) => {
      if (!res.payload && !!res.error) {
        openModal(ConfirmDuplicateModal, {
          message: message ?? t(`Such a record already exists!`),
        });
      } else {
        dispatch(updateFunc(updateFetchParams));
      }
    });
  };

  const updateListAfterAction = () => {
    dispatch(getTagControlListThunk(fetchParams));
  };

  const createNewSectionItem = (newItem: ControlPanelItemFormValues) => {
    createItemWithModalWrapper(
      createTagControlThunk,
      getTagControlListThunk,
      {
        name: newItem.name,
      },
      fetchParams,
    );
  };

  const editSectionItem = (sectionItem: ControlPanelItemFormValues) => {
    createItemWithModalWrapper(
      updateTagControlThunk,
      getTagControlListThunk,
      sectionItem,
      fetchParams,
    );
  };

  const deleteSectionItem = (sectionItem: ControlPanelItemFormValuesWithId) => {
    dispatch(deleteTagControlThunk(sectionItem.id)).then(() =>
      updateListAfterAction(),
    );
  };

  const handleSort = (sortOrder: SortTypes) => {
    setOrderSort(sortOrder === SortTypes.ASC ? SortTypes.DESC : SortTypes.ASC);
  };

  const handleSortChange = (sortOrder: SortTypes) => {
    handleSort(sortOrder);

    dispatch(
      getTagControlListThunk({
        page: 1,
        q: searchQuery,
        sort: `${sortOrder === SortTypes.ASC ? '-' : ''}name`,
      }),
    );
  };

  const handleSearch = (value: string) => {
    setLoading(true);

    dispatch(
      getTagControlListThunk({
        q: value,
        page: 1,
        sort: `${orderSort === SortTypes.DESC ? '-' : ''}name`,
      }),
    ).finally(() => setLoading(false));
  };

  const debounceSearchValue = useCallback(
    debounce(handleSearch, 300) as Function,
    [],
  );

  const updateItems = (items: Tag[]) => {
    dispatch(setTagControlList(items));
  };

  useEffect(() => {
    dispatch(getTagControlListThunk(fetchParams));
  }, []);

  if (!viewable) return null;

  return (
    <CardComponent>
      <StyledControlSectionContainer>
        <StyledControlSectionHeader>
          <StyledHeaderTitle>
            <StyledHeaderLabel>{t('Tag management')}</StyledHeaderLabel>

            <StyledSortContainer onClick={() => handleSortChange(orderSort)}>
              <SortArrowsIcon sortOrder={orderSort} svgId={sectionType} />
            </StyledSortContainer>
          </StyledHeaderTitle>

          <StyledHeaderButtonsWrapper>
            {creatable && (
              <ButtonComponent
                handleClick={() =>
                  openModal(ControlCreateEditModal, {
                    sectionType,
                    createNewSectionItem,
                    editable,
                    deletable,
                  })
                }
                textColor="#588fff"
                customColor="#ebf0ff"
              >
                {t('Create')}
              </ButtonComponent>
            )}
          </StyledHeaderButtonsWrapper>
        </StyledControlSectionHeader>

        <StyledSearchWrapper>
          <InputComponent
            value={searchQuery}
            onChange={(searchValue: string) => {
              setSearchQuery(searchValue);
              debounceSearchValue(searchValue);
            }}
            onEnter={handleSearch}
            placeholder={t(`Search ${sectionType}`)}
            right={
              <StyledSearchIcon>
                {isLoading ? <SpinnerComponent size="small" /> : <SearchIcon />}
              </StyledSearchIcon>
            }
          />
        </StyledSearchWrapper>
        <ControlSectionContent
          isLoadMode={!(pagination?.currentPage === pagination?.lastPage)}
          initialData={tags}
          fetchData={getTagControlListThunk}
          sectionType={sectionType}
          fetchParams={{
            q: searchQuery,
            sort: `${orderSort === SortTypes.DESC ? '-' : ''}name`,
            page: pagination?.currentPage || 1,
          }}
          updateItems={updateItems}
          renderItem={item => (
            <StyledControlListItem
              key={item.id}
              highlighted={item.isAllRegions}
              onClick={() =>
                openModal(ControlCreateEditModal, {
                  sectionType,
                  sectionItem: item,
                  editSectionItem,
                  deleteSectionItem,
                  editable,
                  deletable,
                })
              }
            >
              {item.truncatedDisplayName || item.name}
            </StyledControlListItem>
          )}
        />
      </StyledControlSectionContainer>
    </CardComponent>
  );
};
